import { TabContext, TabList, TabPanel } from "@mui/lab";
import {
  AppBar,
  Box,
  ButtonGroup,
  Card,
  CardContent,
  Chip,
  Container,
  Paper,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Toolbar,
  Typography,
} from "@mui/material";
import { createFileRoute } from "@tanstack/react-router";
import { type FC, type SyntheticEvent, useState } from "react";
import { useTranslation } from "react-i18next";
import { ArchivedProposals } from "../../../../../components/molecules/ArchivedProposals";
import { DownloadProposals } from "../../../../../components/molecules/DownloadProposals";
import { ProposalAudit } from "../../../../../components/molecules/ProposalAudit";
import { fetchProposal } from "../../../../../services/proposals";
import { i18nextParseToDate } from "../../../../../utils/i18nextParseTo";

const ShowProposal: FC = () => {
  const proposal = Route.useLoaderData();
  const { t } = useTranslation();

  const [value, setValue] = useState("0");

  const handleChange = (_event: SyntheticEvent, newValue: string): void => {
    setValue(newValue);
  };

  const costBreakdownLines = proposal.proposal_lines
    .filter((line) => line.service_line_id == null)
    .sort((a, b) => a.position - b.position);
  return (
    <Box
      component="main"
      sx={{
        display: "flex",
        flexDirection: "column",
        flexGrow: 1,
        height: "100%",
        justifyContent: "center",
        px: 2,
        width: "100%",
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexGrow: 1,
          padding: "0px 20px",
        }}
      >
        <div>
          <h2>{proposal.vendor?.fullvendorname}</h2>
          <h3 style={{ marginTop: "-10px" }}>
            {proposal?.service_quotation?.identifier} -{" "}
            {proposal.short_description}
          </h3>
        </div>
      </Box>
      <Container
        sx={{
          height: "100%",
          width: "100%",
        }}
      >
        <Box sx={{ flexGrow: 1 }}>
          <AppBar color="white" position="static">
            <Toolbar variant="dense">
              <Typography component="div" sx={{ flexGrow: 1 }} variant="h6">
                {t("details")}
              </Typography>
              <ButtonGroup
                aria-label="Small button group"
                size="small"
                sx={{ gap: "5px" }}
              >
                <DownloadProposals proposal={proposal} />
              </ButtonGroup>
            </Toolbar>
          </AppBar>
        </Box>
        <Card square variant="outlined">
          <CardContent sx={{ p: 3 }}>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-evenly",
                mb: 1,
              }}
            >
              <Chip
                label={
                  proposal?.lead_time != null
                    ? `${t("newProposal.leadTimeDays")}: ${proposal.lead_time}`
                    : `${t("newProposal.deliveryDate")}: ${
                        proposal.delivery_date
                          ? t(
                              "intlDate",
                              i18nextParseToDate(proposal.delivery_date),
                            )
                          : "-"
                      }`
                }
              />
              <Chip
                label={`${t("newProposal.createdAt")}: ${t("intlDate", i18nextParseToDate(proposal.created_at))}`}
              />
              <Chip
                label={`${t("newProposal.updatedAt")}: ${t("intlDate", i18nextParseToDate(proposal.updated_at))}`}
              />
            </Box>
            <TableContainer component={Paper}>
              <Table aria-label="simple table" sx={{ minWidth: 650 }}>
                <TableHead>
                  <TableRow>
                    <TableCell>
                      {t("serviceNegotiation.shortDescription")}
                    </TableCell>
                    <TableCell align="right">
                      {t("newProposal.quantity")}
                    </TableCell>
                    <TableCell align="right">
                      {t("newProposal.measureUnit")}
                    </TableCell>
                    <TableCell align="right">
                      {t("serviceNegotiation.grossPrice")}
                    </TableCell>
                    <TableCell align="right">{t("common.total")}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {proposal.service_quotation?.service_lines
                    ?.slice()
                    .sort(
                      (a, b) =>
                        (a.service_line_number_ext ?? 0) -
                        (b.service_line_number_ext ?? 0),
                    )
                    .flatMap((serviceLine) =>
                      proposal.proposal_lines
                        .filter(
                          (proposalLine) =>
                            proposalLine.service_line_id != null &&
                            String(proposalLine.service_line_id) ===
                              String(serviceLine.id),
                        )
                        .sort((a, b) => a.position - b.position)
                        .map((proposalLine) => (
                          <TableRow key={proposalLine.id}>
                            <TableCell>{proposalLine.short_text}</TableCell>
                            <TableCell align="right">
                              {proposalLine.quantity}
                            </TableCell>
                            <TableCell align="right">
                              {proposalLine.measure_unit?.name}
                            </TableCell>
                            <TableCell align="right">
                              {proposal.currency.unit}{" "}
                              {proposalLine.gross_price}
                            </TableCell>
                            <TableCell align="right">
                              {proposal.currency.unit}{" "}
                              {(
                                (proposalLine.gross_price ?? 0) *
                                (proposalLine.quantity ?? 0)
                              ).toFixed(2)}
                            </TableCell>
                          </TableRow>
                        )),
                    )}

                  {costBreakdownLines.length > 0 && (
                    <>
                      <TableRow sx={{ bgcolor: "grey.200" }}>
                        <TableCell
                          colSpan={5}
                          sx={{ fontWeight: "bold", py: 1 }}
                        >
                          {t("newProposal.costBreakdown")}
                        </TableCell>
                      </TableRow>
                      {costBreakdownLines.map((line) => (
                        <TableRow key={line.id}>
                          <TableCell>{line.short_text}</TableCell>
                          <TableCell align="right">{line.quantity}</TableCell>
                          <TableCell align="right">
                            {line.measure_unit?.name}
                          </TableCell>
                          <TableCell align="right">
                            {proposal.currency.unit} {line.gross_price}
                          </TableCell>
                          <TableCell align="right">
                            {proposal.currency.unit}{" "}
                            {(
                              (line.gross_price ?? 0) * (line.quantity ?? 0)
                            ).toFixed(2)}
                          </TableCell>
                        </TableRow>
                      ))}
                    </>
                  )}

                  <TableRow
                    sx={{
                      "&:last-child td, &:last-child th": { border: 0 },
                    }}
                  >
                    <TableCell colSpan={3} />
                    <TableCell align="right" sx={{ fontWeight: "bold" }}>
                      {t("newProposal.grandTotal")}
                    </TableCell>
                    <TableCell align="right">
                      {proposal.currency.unit} {proposal.price}
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      </Container>

      <Container>
        <TabContext value={value}>
          <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
            <TabList aria-label="basic tabs example" onChange={handleChange}>
              <Tab label={t("proposal.archivedProposals")} value="0" />
              <Tab label={t("serviceNegotiation.audits")} value="1" />
            </TabList>
          </Box>
          <TabPanel value="0">
            <Container
              sx={{ marginTop: "5px", minHeight: "400px", width: "100%" }}
            >
              <ArchivedProposals proposal={proposal} />
            </Container>
          </TabPanel>
          <TabPanel value="1">
            <Container sx={{ marginTop: "5px", minHeight: "450px" }}>
              <ProposalAudit proposal={proposal} />
            </Container>
          </TabPanel>
        </TabContext>
      </Container>
    </Box>
  );
};

export const Route = createFileRoute("/_private/vbuyer/proposals/$proposalId/")(
  {
    component: ShowProposal,
    loader: ({ params: { proposalId } }) => fetchProposal(proposalId),
  },
);
